import { DateTime } from 'luxon';
import { MEDIA_HOST } from '../config';
import { ItemDetailFieldsFragment, View } from './graphql-sdk';
import { allCategoryId } from 'redusers/selected-item-category-reducer';

const getDisplayedOnValidity = (displayedOn: View[], pageType: View) => {
  const isValid = displayedOn.includes(pageType);
  return isValid;
};

const getPreBookValidity = (
  date: string | null,
  item: ItemDetailFieldsFragment,
  timezone?: string
) => {
  if (!date) return false;

  let preBookDate;

  // check if item has a custom cutoff duration specified
  if (item.useCustomPreBookCutoff && item.preBookCutoff) {
    let hoursToSubtract = item.preBookCutoff;

    // better safe than sorry
    if (Number.isNaN(Number(hoursToSubtract))) {
      hoursToSubtract = 48;
    }

    preBookDate = DateTime.fromISO(date)
      .setZone(timezone)
      .minus({ hours: hoursToSubtract })
      .toUTC()
      .toISO();
  } else {
    // otherwise, use the regular 24h + extra 24h for sunday bookings
    const daysToSubtract = getWeekday(date, timezone) === 0 ? 2 : 1;

    preBookDate = DateTime.fromISO(date)
      .setZone(timezone)
      .minus({ days: daysToSubtract })
      .set({ hour: 11, minute: 0, second: 0, millisecond: 0 })
      .toUTC()
      .toISO();
  }

  const now = DateTime.utc().toISO();

  if (now && preBookDate) {
    return now < preBookDate;
  }

  return false;
};

const getWeekday = (date: string, timezone?: string) => {
  let weekday = DateTime.fromISO(date).setZone(timezone).weekday;
  if (weekday === 7) weekday = 0;

  return weekday;
};

const getDaysValidity = (
  days: Array<{
    weekday?: string | null;
    available?: boolean | null;
  }>,
  date: string | null,
  timezone?: string
) => {
  if (!date) return false;

  const isValid = days[getWeekday(date, timezone)]?.available;
  return isValid;
};

const getTimesValidity = (
  times: {
    after?: string | null;
    before?: string | null;
  } | null,
  date: string | null,
  timezone?: string
) => {
  if (!date) return false;

  const startTime = DateTime.fromISO(date).setZone(timezone);

  let afterTime;
  let isAfter;
  if (times?.after) {
    const [afterHoursString, afterMinutesString] = times.after.split(':');

    const afterHours = Number(afterHoursString);
    const afterMinutes = Number(afterMinutesString);

    if (!Number.isNaN(afterHours) && !Number.isNaN(afterMinutes)) {
      // Only set afterTime and isAfter if time is valid
      afterTime = DateTime.fromISO(date)
        .setZone(timezone)
        .set({ hour: Number(afterHours), minute: Number(afterMinutes) });

      isAfter = startTime.toMillis() >= afterTime.toMillis();
    }
  }

  let beforeTime;
  let isBefore;
  if (times?.before) {
    const [beforeHoursString, beforeMinutesString] = times.before.split(':');

    const beforeHours = Number(beforeHoursString);
    const beforeMinutes = Number(beforeMinutesString);

    if (!Number.isNaN(beforeHours) && !Number.isNaN(beforeMinutes)) {
      // Only set beforeTime and isBefore if time is valid
      beforeTime = DateTime.fromISO(date)
        .setZone(timezone)
        .set({ hour: Number(beforeHours), minute: Number(beforeMinutes) });

      isBefore = startTime.toMillis() <= beforeTime.toMillis();
    }
  }

  let isValid;
  if (times?.after && times?.before) {
    isValid = isAfter && isBefore;
  } else if (times?.after && !times?.before) {
    isValid = isAfter;
  } else if (!times?.after && times?.before) {
    isValid = isBefore;
  } else {
    isValid = true;
  }

  return isValid;
};

const getDurationValidity = (
  duration: {
    min?: number | null;
    max?: number | null;
  } | null,
  bookingDuration: number
) => {
  let minValid = true;
  if (duration?.min && typeof duration.min === 'number') {
    minValid = bookingDuration >= duration.min;
  }

  let maxValid = true;
  if (duration?.max && typeof duration.max === 'number') {
    maxValid = bookingDuration <= duration.max;
  }

  const isValid = minValid && maxValid;

  return isValid;
};

const getCategoryValidity = (
  item: ItemDetailFieldsFragment,
  selectedCategoryId: string
) => {
  if (selectedCategoryId === allCategoryId) return true;

  return item.categoryNames.some((categoryName) => {
    const categoryId = getItemCategoryId(categoryName);
    return selectedCategoryId === categoryId;
  });
};

interface GetFilteredItemsProps {
  items: ItemDetailFieldsFragment[];
  bookingDate: string | null;
  timezone?: string;
  selectedCategoryId: string;
  pageType: View;
  duration: number;
}

export const getFilteredItems = ({
  items,
  bookingDate,
  timezone,
  selectedCategoryId,
  pageType,
  duration,
}: GetFilteredItemsProps) => {
  const filteredItems = items
    .filter((item) => {
      let shouldBeIncluded = true;

      if (
        item.displayedOn?.length > 0 &&
        !getDisplayedOnValidity(item.displayedOn, pageType)
      ) {
        shouldBeIncluded = false;
      }

      if (item.preBook && !getPreBookValidity(bookingDate, item, timezone)) {
        shouldBeIncluded = false;
      }

      if (item.days?.length > 0 && !getDaysValidity(item.days, bookingDate, timezone)) {
        shouldBeIncluded = false;
      }

      if (item.times && !getTimesValidity(item.times, bookingDate, timezone)) {
        shouldBeIncluded = false;
      }

      if (item.duration && !getDurationValidity(item.duration, duration)) {
        shouldBeIncluded = false;
      }

      if (!getCategoryValidity(item, selectedCategoryId)) {
        shouldBeIncluded = false;
      }

      return shouldBeIncluded;
    })
    .sort((a, b) => (a.order ?? items.length) - (b.order ?? items.length));

  return filteredItems;
};

export const getItemImageUrl = (
  originalImagePath: string | null | undefined,
  width: number
) => {
  const imagePath = `${MEDIA_HOST}${originalImagePath}?tr=w-${width},q=80`;

  return imagePath;
};

export const getItemCategoryId = (categoryName: string) => {
  return categoryName.replace(/\s+/g, '-').toLowerCase();
};
